import type { IInputOption } from 'components/form/input/types';
import Icon from 'components/icon';
import { twColor } from '@/utils/style';
import { EAssetMaintainer, getRefId, type ILiability } from 'findef';
import { Grid } from 'components/grid';
import { prettyNumber } from '@/utils/number/format';
import TinyBadge from 'components/tiny-badge';
import { getMaintainedText } from '@/utils/liability/maintained';
import { useResponsive } from '~/composables/useResponsive';

export const liabilityToOption = (liability: ILiability): IInputOption => {
  const responsive = useResponsive();
  const providerImport = liability?.providerImport;
  const provider =
    providerImport && typeof providerImport !== 'string' && 'provider' in (providerImport as any)
      ? (providerImport as any).provider
      : undefined;

  const maintainedText =
    getMaintainedText(liability) === EAssetMaintainer.PROVIDER
      ? provider?.displayName || EAssetMaintainer.PROVIDER.toLocaleLowerCase()
      : EAssetMaintainer.MANUAL.toLocaleLowerCase();

  const liabilityTypeText = liability?.type?.toLowerCase();
  const liabilityAmountText = prettyNumber(liability?.amount?.value || 0, {
    american: true,
    maxDecimals: 2,
    zeroIsInteger: true,
    suffix: liability?.amount?.type || '',
  });

  return {
    value: liability,
    key: getRefId(liability),
    label: liability.name ?? '',
    textClass: 'text-base font-normal leading-normal font-["Inter"] leading-[18px]',
    body: () => {
      return (
        <Grid
          class="w-full h-full"
          columns="max-content max-content"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid columns="max-content 1fr" alignItems="center" gap="0.625rem">
            {!responsive.isSmall.value &&
              <div
                class={['h-full justify-center items-center flex', 'bg-gray-100', 'w-10 h-10']}
                style={{ aspectRatio: '1 / 1' }}
              >
                <div
                  class={[
                    'justify-center relative items-center flex',
                    'w-4 h-5',
                    'p-0 w-full h-full',
                  ]}
                >
                  <Icon
                    size="1.25rem"
                    stroke="none"
                    fill={twColor('gray-400')}
                    src={async () => import('assets/img/svg/icons/receipt.svg?raw')}
                  />
                </div>
              </div>
            }

            <div class="flex-col justify-start items-start inline-flex">
              <div class="text-[#111928] text-xs font-semibold ">{liability?.name}</div>
              <div class="text-gray-500 text-xs capitalize">{liabilityTypeText}</div>
            </div>
          </Grid>
          <Grid columns="max-content 1fr" alignItems="center" gap="0.625rem">
            <TinyBadge type="mltslct">
              <div class="text-center text-gray-500 text-xs font-semibold">
                {liabilityAmountText}
              </div>
            </TinyBadge>
            {!responsive.isSmall.value &&
              <TinyBadge type="mltslct">
                <div class="flex items-center gap-1">
                  {provider?.iconUrl && (
                    <img
                      alt={provider?.name ?? 'provider'}
                      class="h-3.5 border border-gray-300 object-cover"
                      src={provider?.iconUrl}
                    />
                  )}
                  <div class="text-center text-gray-500 text-xs font-semibold font-['Inter'] leading-[18px] capitalize">
                    {maintainedText}
                  </div>
                </div>
              </TinyBadge>
            }
          </Grid>
        </Grid>
      );
    },
  };
};
